/**
 * Fridges & Freezers — fast temp entry helpers (OPEN → ENTER → SUBMIT → NEXT).
 */

import {
  FOOD_SAFETY_FRIDGE_MAX_DUE_TIMES,
  FOOD_SAFETY_FRIDGE_MAX_LOGS_PER_DAY,
} from 'src/constants/foodSafety'
import { formatFoodSafetyClockTime } from 'src/utils/foodSafetyTime'
import {
  formatFoodSafetyTempRange,
  formatTempValue,
} from 'src/utils/foodSafetyTempRange'

/**
 * Parse a temp draft into a number (or null while incomplete).
 * Supports a leading minus for freezers (and out-of-range fridge lows).
 * @param {string|number|null|undefined} text
 * @returns {number|null}
 */
export function parseFridgeTempInput(text) {
  if (typeof text === 'number') {
    return Number.isFinite(text) ? text : null
  }
  const trimmed = String(text ?? '').trim()
  if (
    trimmed === '' ||
    trimmed === '-' ||
    trimmed === '.' ||
    trimmed === '-.'
  ) {
    return null
  }
  const num = Number(trimmed)
  if (Number.isNaN(num)) return null
  return num
}

/**
 * Toggle a leading minus on a temp draft (mobile keyboards often omit −).
 * Empty → "-"; "-" → ""; "-12" → "12"; "12" → "-12".
 */
export function toggleFridgeTempSign(text) {
  const raw = String(text ?? '')
  if (raw === '' || raw === '.') return '-'
  if (raw === '-') return ''
  if (raw === '-.') return '.'
  if (raw.startsWith('-')) return raw.slice(1)
  return `-${raw}`
}

/**
 * @param {number|null|undefined} value
 * @param {{ tempMin?: number, tempMax?: number }|null|undefined} config
 * @returns {boolean}
 */
export function isFridgeTempOutOfRange(value, config) {
  if (value == null || value === '' || Number.isNaN(Number(value))) {
    return false
  }
  const num = Number(value)
  if (config?.tempMin != null && num < Number(config.tempMin)) return true
  if (config?.tempMax != null && num > Number(config.tempMax)) return true
  return false
}

/**
 * Display OOR for a stored fridge log — always recompute from value + range.
 * Prefer `configSnapshot` (range at log time); fall back to live definition config.
 */
export function resolveFridgeLogOutOfRange(log, fallbackConfig) {
  if (!log || log?.payload?.skipped === true) return false
  const config = log?.configSnapshot || fallbackConfig
  return isFridgeTempOutOfRange(log?.payload?.value, config)
}

export function formatFridgeDueBy(dueByTime) {
  if (!dueByTime || typeof dueByTime !== 'string') return null
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(dueByTime)
  if (!match) return `Due by ${dueByTime}`
  return `Due by ${match[1]}:${match[2]}`
}

const DUE_TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/

export function normalizeFridgeDueTimes(times) {
  const seen = new Set()
  const out = []
  for (const raw of Array.isArray(times) ? times : []) {
    const time = String(raw || '').trim()
    if (!DUE_TIME_RE.test(time) || seen.has(time)) continue
    seen.add(time)
    out.push(time)
  }
  return out.slice(0, FOOD_SAFETY_FRIDGE_MAX_DUE_TIMES)
}

export function fridgeDueTimesFromDefinition(definition) {
  const stored = normalizeFridgeDueTimes(definition?.dueTimes)
  if (stored.length) return stored
  const dueBy = String(definition?.dueByTime || '').trim()
  return DUE_TIME_RE.test(dueBy) ? [dueBy] : []
}

const shiftDueHours = (time, hours) => {
  const [h, m] = time.split(':').map(Number)
  const next = (((h + hours) % 24) + 24) % 24
  return `${String(next).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

export function suggestNextFridgeDueTime(dueTimes = []) {
  const existing = new Set(dueTimes)
  const last = dueTimes[dueTimes.length - 1] || '10:00'
  const guesses = ['15:00', '12:00', '18:00', '21:00', shiftDueHours(last, 6)]
  return guesses.find(time => !existing.has(time)) || shiftDueHours(last, 1)
}

export function addFridgeDueTime(dueTimes = []) {
  const current = [...dueTimes]
  if (current.length >= FOOD_SAFETY_FRIDGE_MAX_DUE_TIMES) return current
  return [...current, suggestNextFridgeDueTime(current)]
}

export function listFridgeDueSlots(item) {
  const times =
    Array.isArray(item?.dueTimes) && item.dueTimes.length
      ? normalizeFridgeDueTimes(item.dueTimes)
      : fridgeDueTimesFromDefinition(item?.definition)
  const apiSlots = Array.isArray(item?.dueSlots) ? item.dueSlots : []
  const logs = listFridgeReadings(item)
  const used = new Set()
  return times.map(dueTime => {
    const api = apiSlots.find(slot => slot.dueTime === dueTime)
    const log =
      logs.find(row => row?.payload?.dueTime === dueTime && !used.has(row)) ||
      null
    if (log) used.add(log)
    return {
      dueTime,
      status: api?.status,
      completed: Boolean(api?.completed || log),
      log,
    }
  })
}

export function listFridgeExtraLogs(item) {
  const slotLogs = new Set(
    listFridgeDueSlots(item)
      .map(slot => slot.log)
      .filter(Boolean),
  )
  return listFridgeReadings(item).filter(log => !slotLogs.has(log))
}

export function fridgeDueSlotsFilled(item) {
  const slots = listFridgeDueSlots(item)
  return slots.length > 0 && slots.every(slot => slot.completed)
}

export function nextUnfinishedFridgeSlotDueTime(item, afterDueTime) {
  const slots = listFridgeDueSlots(item)
  if (!slots.length) return null
  const start = afterDueTime
    ? slots.findIndex(slot => slot.dueTime === afterDueTime) + 1
    : 0
  const next =
    slots.slice(Math.max(0, start)).find(slot => !slot.completed) ||
    slots.find(slot => !slot.completed)
  return next?.dueTime || null
}

export function unfinishedFridgeSlotLabels(items = []) {
  const labels = []
  for (const item of items) {
    const name = String(item?.definition?.name || '').trim()
    for (const slot of listFridgeDueSlots(item)) {
      if (slot.completed) continue
      labels.push(name ? `${name} · ${slot.dueTime}` : slot.dueTime)
    }
  }
  return labels
}

export function formatFridgeStillToLogFromLabels(names = []) {
  const labels = (names || []).filter(Boolean)
  if (!labels.length) return ''
  if (labels.length === 1) return `Still to log: ${labels[0]}`
  const shown = labels.slice(0, 4)
  const extra = labels.length - shown.length
  const list = shown.join(', ')
  return extra > 0
    ? `Still to log: ${list} +${extra} more`
    : `Still to log: ${list}`
}

export function formatFridgeUnfinishedSlotsCue(items = []) {
  return formatFridgeStillToLogFromLabels(unfinishedFridgeSlotLabels(items))
}

export function formatFridgeUnitType(unitType) {
  if (unitType === 'freezer') return 'Freezer'
  if (unitType === 'fridge') return 'Fridge'
  return null
}

export function formatLoggedTemp(log) {
  const value = log?.payload?.value
  if (value == null || value === '') return null
  return `${Number(value)}°C`
}

/**
 * First incomplete scheduled item (for NEXT focus).
 * @param {Array<{ definition?: {_id?: string}, completed?: boolean, expectation?: { due?: boolean, status?: string } }>} items
 * @returns {string|null}
 */
function isOpenFridgeItem(item) {
  if (item?.completed) return false
  if (item?.expectation?.due === false) return false
  return item?.expectation?.status !== 'completed'
}

export function listOpenFridgeDefinitionIds(items) {
  if (!Array.isArray(items)) return []
  return items
    .filter(isOpenFridgeItem)
    .map(item => item?.definition?._id)
    .filter(Boolean)
    .map(id => String(id))
}

export function findNextOpenFridgeDefinitionId(items) {
  const openIds = listOpenFridgeDefinitionIds(items)
  return openIds[0] || null
}

/**
 * Card that gets the lime "next" frame: Express's next unit (or the first
 * open one), skipping anything already missed — a missed unit says Missed,
 * not "do this next". Null when every open unit is missed.
 */
export function findFramedNextDefinitionId(items, preferredId) {
  if (!Array.isArray(items)) return null
  const ids = items
    .filter(item => isOpenFridgeItem(item))
    .filter(item => item?.expectation?.status !== 'missed')
    .map(item => String(item?.definition?._id || ''))
    .filter(Boolean)
  const preferred = preferredId ? String(preferredId) : ''
  if (preferred && ids.includes(preferred)) return preferred
  return ids[0] || null
}

/**
 * Due-by time ("10:00") when the day's first reading came after it, or
 * was saved on a later day. Null when on time or not scheduled.
 */
export function resolveFoodSafetyLateDueBy(item) {
  if (item?.expectation?.due === false) return null
  const dueByTime = item?.definition?.dueByTime
  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(String(dueByTime || ''))) return null
  const first = listFridgeReadings(item)
    .filter(log => log?.payload?.skipped !== true && log?.completedAt)
    .sort(
      (a, b) =>
        new Date(a.completedAt).getTime() - new Date(b.completedAt).getTime(),
    )[0]
  if (!first) return null
  const late =
    first.loggedLate === true ||
    formatFoodSafetyClockTime(first.completedAt) > dueByTime
  return late ? dueByTime : null
}

/**
 * Next open fridge after `afterDefinitionId` (Enter advance).
 * @param {Array} items
 * @param {string|null|undefined} afterDefinitionId
 * @param {{ reverse?: boolean }} [opts]
 * @returns {string|null}
 */
export function findNextOpenFridgeDefinitionIdAfter(
  items,
  afterDefinitionId,
  { reverse = false } = {},
) {
  const openIds = listOpenFridgeDefinitionIds(items)
  if (!openIds.length) return null
  if (!afterDefinitionId)
    return reverse ? openIds[openIds.length - 1] : openIds[0]
  const idx = openIds.indexOf(String(afterDefinitionId))
  if (idx < 0) return reverse ? openIds[openIds.length - 1] : openIds[0]
  if (reverse) return idx > 0 ? openIds[idx - 1] : null
  return idx < openIds.length - 1 ? openIds[idx + 1] : null
}

/**
 * Sticky strip copy. Before the due time every open card is "Not due yet",
 * so the strip says "4 waiting". After that time it is "4 missed". A mix is
 * "2 missed · 1 waiting". Null when nothing is open.
 */
export function formatFridgeOpenLeftLabel(openCount, waitingCount = 0) {
  const open = Number(openCount) || 0
  if (open <= 0) return null
  const waiting = Math.min(open, Math.max(0, Number(waitingCount) || 0))
  const missed = open - waiting
  const waitingLabel =
    waiting === 1 ? '1 waiting' : waiting > 0 ? `${waiting} waiting` : null
  const missedLabel =
    missed === 1 ? '1 missed' : missed > 0 ? `${missed} missed` : null
  if (missedLabel && waitingLabel) return `${missedLabel} · ${waitingLabel}`
  return missedLabel || waitingLabel
}

/**
 * Due-aware day progress (29.42) — off-day / not-due units don’t inflate
 * open or X of Y. When due times exist, X of Y is one check per slot
 * (cleaning reuses this with no slots and stays per unit).
 * @param {Array<{ completed?: boolean, expectation?: { due?: boolean } }>} items
 */
export function summarizeFridgeDayProgress(items) {
  const list = Array.isArray(items) ? items : []
  const dueItems = list.filter(i => i?.expectation?.due)
  const slots = dueItems.flatMap(item => listFridgeDueSlots(item))
  const dueTotal = slots.length ? slots.length : dueItems.length
  const doneCount = slots.length
    ? slots.filter(slot => slot.completed).length
    : dueItems.filter(i => i.completed).length
  const openCount = listOpenFridgeDefinitionIds(list).length
  const waitingCount = list.filter(
    item =>
      isOpenFridgeItem(item) && item?.expectation?.status === 'not_due_yet',
  ).length
  const earlyLoggedCount = list.filter(
    i => i.completed && i?.expectation?.due === false,
  ).length
  return { dueTotal, doneCount, openCount, waitingCount, earlyLoggedCount }
}

/** Hero / subtitle progress — never “1 of 3” when only 0 are due today. */
export function formatFridgeDayProgressLabel({
  dueTotal = 0,
  doneCount = 0,
  earlyLoggedCount = 0,
} = {}) {
  if (dueTotal > 0) return `${doneCount} of ${dueTotal} done`
  if (earlyLoggedCount > 0) {
    return `${earlyLoggedCount} logged · not due today`
  }
  return null
}

/**
 * Body for POST /logs fridge temp submit.
 */
export function buildFridgeTempLogBody({
  definitionId,
  localDate,
  value,
  correctiveAction,
  readingPeriod,
  dueTime,
}) {
  const payload = { value: Number(value) }
  if (readingPeriod) payload.readingPeriod = readingPeriod
  if (dueTime) payload.dueTime = dueTime
  const body = { definitionId, localDate, payload }
  if (correctiveAction && typeof correctiveAction === 'object') {
    body.correctiveAction = correctiveAction
    return body
  }
  const note = String(correctiveAction || '').trim()
  if (note) payload.correctiveAction = note
  return body
}

export function fridgeRangeHint(config) {
  const min = config?.tempMin
  const max = config?.tempMax
  if (min != null && max != null)
    return `Range ${formatFoodSafetyTempRange(min, max)}`
  if (min != null) return `Min ${formatTempValue(min)}°C`
  if (max != null) return `Max ${formatTempValue(max)}°C`
  return null
}

/** The day's readings — rechecks show under their reading, not as one. */
export function listFridgeReadings(item) {
  return (Array.isArray(item?.logs) ? item.logs : []).filter(
    log => !log?.recheckOfException,
  )
}

export function fridgeLogCount(item) {
  return listFridgeReadings(item).length
}

export function canAddFridgeTempLog(item) {
  return fridgeLogCount(item) < FOOD_SAFETY_FRIDGE_MAX_LOGS_PER_DAY
}

export const FOOD_SAFETY_FRIDGE_ADD_READING_LABEL =
  'Add another temperature reading'

export const FOOD_SAFETY_FRIDGE_READING_PERIOD = Object.freeze({
  MORNING: 'morning',
  AFTERNOON: 'afternoon',
  EXTRA: 'extra',
})

export const FOOD_SAFETY_FRIDGE_MORNING_LABEL = 'Morning'
export const FOOD_SAFETY_FRIDGE_AFTERNOON_LABEL = 'Afternoon'
export const FOOD_SAFETY_FRIDGE_TWICE_DAILY_LABEL =
  'Morning and afternoon checks'
export const FOOD_SAFETY_FRIDGE_TWICE_DAILY_HINT = `Each fridge is due a morning reading and an afternoon reading. Leave this off for Daily — due once, and staff can still add extra readings (up to ${FOOD_SAFETY_FRIDGE_MAX_LOGS_PER_DAY}).`

export function fridgeDraftKey(definitionId, period) {
  const id = String(definitionId || '')
  return period ? `${id}:${period}` : id
}

/**
 * Morning and afternoon slots. The first reading saved before slots existed
 * stands in for morning. Extra readings stay on the add-another path.
 */
export function splitFridgeReadings(
  item,
  { morningAndAfternoon = false } = {},
) {
  const readings = listFridgeReadings(item)
  if (!morningAndAfternoon) {
    return { morning: null, afternoon: null, extras: readings }
  }
  let morning =
    readings.find(
      log =>
        log?.payload?.readingPeriod ===
        FOOD_SAFETY_FRIDGE_READING_PERIOD.MORNING,
    ) || null
  const afternoon =
    readings.find(
      log =>
        log?.payload?.readingPeriod ===
        FOOD_SAFETY_FRIDGE_READING_PERIOD.AFTERNOON,
    ) || null
  const extras = []
  let unslottedUsed = Boolean(morning)
  for (const log of readings) {
    const period = log?.payload?.readingPeriod
    if (period === FOOD_SAFETY_FRIDGE_READING_PERIOD.MORNING) continue
    if (period === FOOD_SAFETY_FRIDGE_READING_PERIOD.AFTERNOON) continue
    if (period === FOOD_SAFETY_FRIDGE_READING_PERIOD.EXTRA) {
      extras.push(log)
      continue
    }
    if (!unslottedUsed) {
      morning = log
      unslottedUsed = true
      continue
    }
    extras.push(log)
  }
  return { morning, afternoon, extras }
}

export function fridgeSlotsFilled(split) {
  return Boolean(split?.morning && split?.afternoon)
}

/** Beside a fridge in setup, and once above the logging list. */
export function formatFridgeFiveReadingsCopy(morningAndAfternoon = false) {
  if (morningAndAfternoon) {
    return `Each due time is its own check. Staff can still add extra readings (up to ${FOOD_SAFETY_FRIDGE_MAX_LOGS_PER_DAY}).`
  }
  return `Each due time is its own check — not one reading for the day. Staff can still add extra readings (up to ${FOOD_SAFETY_FRIDGE_MAX_LOGS_PER_DAY}).`
}

/** Position of the reading being entered, e.g. "Reading 2 of up to 5". */
export function formatFridgeReadingPosition(item) {
  return `Reading ${
    fridgeLogCount(item) + 1
  } of up to ${FOOD_SAFETY_FRIDGE_MAX_LOGS_PER_DAY}`
}

export function formatFridgeReadingsLimitHint() {
  return `${FOOD_SAFETY_FRIDGE_MAX_LOGS_PER_DAY} of up to ${FOOD_SAFETY_FRIDGE_MAX_LOGS_PER_DAY} readings logged — that’s the limit for one day`
}

/** Badge when at least one temp is logged today. */
export function formatFridgeLoggedBadge(item) {
  const n = fridgeLogCount(item)
  if (n <= 0) return null
  const lateDue = resolveFoodSafetyLateDueBy(item)
  const label = lateDue ? `Logged late (due ${lateDue})` : 'Logged'
  return n === 1 ? label : `${label} · ${n}`
}

export function formatFridgeLogTime(completedAt) {
  if (!completedAt) return ''
  return formatFoodSafetyClockTime(completedAt)
}

export { FOOD_SAFETY_FRIDGE_MAX_LOGS_PER_DAY }
